import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, CreditCard, FileUp, Loader2, Send, Truck, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { plur } from "../lib/localize";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { FadeUp } from "../components/motion";
import { SEO } from "../components/SEO";
import { SmsEtapesBox } from "../components/SmsEtapesBox";

const MAX_LOGO = 25 * 1024 * 1024;  // 25 Mo max (demande client)
const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return Number.isFinite(n) && n > 0 ? n : 0; };
const money = (v) => `${(Math.round(v * 100) / 100).toFixed(2)} $`;

// Portail de réponse client (/soumission/:token, lien du courriel — sans compte) :
// le client CHOISIT ses produits en cliquant dessus, inscrit le texte de
// gravure/impression de chacun et téléverse son logo (25 Mo max) → tout part par
// courriel à commande@trophee.ca et dans l'onglet Soumissions de l'admin.
export default function QuoteRespond() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [payBusy, setPayBusy] = useState(false);
  const [q, setQ] = useState(null);        // null = chargement, false = lien invalide
  const [sel, setSel] = useState([]);      // indices des produits choisis
  const [texts, setTexts] = useState({});  // {indice: texte de gravure}
  const [methods, setMethods] = useState({});  // {indice: "laser" | "serigraphie"} — type d'inscription choisi par le client
  const [logos, setLogos] = useState([]);  // pièces jointes du client [{url, name}] — PLUSIEURS possibles (demande du propriétaire)
  const [logoBusy, setLogoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [refuseOpen, setRefuseOpen] = useState(false);   // popup « Refuser la soumission »
  const [refuseMotif, setRefuseMotif] = useState("");
  const [refuseNote, setRefuseNote] = useState("");
  const [refuseBusy, setRefuseBusy] = useState(false);
  const [refused, setRefused] = useState(false);
  // Retrait de la soumission par le client (demande du propriétaire, 2026-10-06) :
  // raison OBLIGATOIRE — met fin à toutes les relances
  const [retireOpen, setRetireOpen] = useState(false);
  const [retireReason, setRetireReason] = useState("");
  const [retireBusy, setRetireBusy] = useState(false);
  const [retired, setRetired] = useState(false);
  const [counterOffer, setCounterOffer] = useState(null);  // % de la contre-offre auto (retrait pour raison de prix)
  const [acceptedBanner, setAcceptedBanner] = useState(false);  // retour du clic « Accepter » du courriel
  const [accepterInvite, setAccepterInvite] = useState(false);  // lien du courriel → bouton de confirmation (2026-10-22)
  const [acceptBusy, setAcceptBusy] = useState(false);
  // « Livrer à » (demande du propriétaire 2026-10-10) : après acceptation, le
  // client confirme son adresse de livraison — case « même adresse » par défaut
  const [livSame, setLivSame] = useState(true);
  const [liv, setLiv] = useState({ name: "", address1: "", address2: "", city: "", province: "QC", postal_code: "" });
  const [livEdit, setLivEdit] = useState(false);
  const [livBusy, setLivBusy] = useState(false);
  // « Facturé à » (demande du propriétaire 2026-10-19) : à l'acceptation, le
  // client CONFIRME son adresse complète — case « copier l'adresse de ma
  // demande » (défaut) OU nouvelle adresse complète
  const [facSame, setFacSame] = useState(true);
  const [fac, setFac] = useState({ name: "", address1: "", address2: "", city: "", province: "QC", postal_code: "" });
  const [facEdit, setFacEdit] = useState(false);
  const [facBusy, setFacBusy] = useState(false);

  useEffect(() => {
    api.get(`/quick-quote/${token}`)
      .then((r) => {
        setQ(r.data);
        const resp = r.data.response || {};
        // Adresse de livraison déjà confirmée : on la recharge telle quelle
        const lv = r.data.livraison_a || {};
        if (lv.address1) {
          setLiv({ name: lv.name || "", address1: lv.address1 || "", address2: lv.address2 || "",
                   city: lv.city || "", province: lv.province || "QC", postal_code: lv.postal_code || "" });
        }
        if (r.data.livraison_confirmee) setLivSame(r.data.livraison_meme_adresse !== false);
        // Adresse de facturation déjà confirmée : on la recharge telle quelle
        const fc = r.data.facture_a || {};
        if (fc.address1) {
          setFac({ name: fc.name || "", address1: fc.address1 || "", address2: fc.address2 || "",
                   city: fc.city || "", province: fc.province || "QC", postal_code: fc.postal_code || "" });
        }
        if (r.data.facture_confirmee) setFacSame(r.data.facture_meme_adresse !== false);
        if (r.data.responded) {
          setSel(resp.selected || []);
          setTexts(resp.texts || {});
          setMethods(resp.methods || {});
        } else {
          // Par défaut TOUT est coché (la soumission couvre tous les modèles) :
          // le client DÉCOCHE ce qu'il ne veut pas et le total se recalcule
          // aussitôt (demande du propriétaire, 2026-10-28)
          setSel((r.data.items || []).map((_, i) => i));
        }
        // Pièces jointes déjà téléversées : visibles MÊME avant l'envoi de la
        // réponse (le client peut téléverser, revenir plus tard et compléter)
        const toutes = [
          ...(resp.logo ? [{ url: resp.logo, name: resp.logo_name }] : []),
          ...(resp.attachments || []).map((a) => (typeof a === "string" ? { url: a, name: "" } : a)),
        ];
        if (toutes.length) setLogos(toutes);
      })
      .catch(() => setQ(false));
  }, [token]);

  // Retour de Stripe (?paye=1) : CONFIRMER le paiement directement auprès de
  // Stripe (correctif 2026-11-01 — le webhook peut manquer, le client voyait
  // « payé » sans que le dossier ne le soit) puis recharger le portail à jour
  // ?refuser=1 : lien « Refuser la soumission » du courriel → popup de refus ouvert
  // ?accepte=1 : retour du bouton « Accepter la soumission » du courriel (un clic)
  useEffect(() => {
    if (params.get("paye") === "1") {
      (async () => {
        try {
          const { data } = await api.post(`/quick-quote/${token}/confirmer-paiement`);
          if (data?.status === "paid") toast.success(t("respond.pay_thanks"));
          else if (data?.status === "partial")
            toast.success(`Dépôt reçu — merci ! Balance à payer : ${Number(data.balance_due || 0).toFixed(2)} $`);
          else toast.success(t("respond.pay_thanks"));
        } catch { toast.success(t("respond.pay_thanks")); }
        api.get(`/quick-quote/${token}`).then((r) => setQ(r.data)).catch(() => {});
      })();
    }
    if (params.get("refuser") === "1") setRefuseOpen(true);
    if (params.get("accepte") === "1") setAcceptedBanner(true);
    // ?accepter=1 : le lien « Accepter » du courriel n'accepte PLUS tout seul
    // (demande du propriétaire, 2026-10-22 — les robots de courriels suivaient le
    // lien) — il ouvre le portail avec un bouton de CONFIRMATION humaine
    if (params.get("accepter") === "1") setAccepterInvite(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (i) => setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  const uploadLogo = async (e) => {
    // PLUSIEURS fichiers en une sélection (ou en plusieurs fois) — 10 max, 25 Mo chacun
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (logos.length + files.length > 10) { toast.error(t("respond.logo_too_many")); return; }
    setLogoBusy(true);
    try {
      for (const file of files) {
        if (file.size > MAX_LOGO) { toast.error(t("respond.logo_hint")); continue; }
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(`/quick-quote/${token}/logo`, form);
        setLogos((xs) => [...xs, { url: data.url, name: data.name }]);
      }
      toast.success(t("respond.logo_ok"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLogoBusy(false);
    }
  };
  const removeLogo = async (j) => {
    try { await api.delete(`/quick-quote/${token}/attachments/${j}`); } catch { /* silencieux */ }
    setLogos((xs) => xs.filter((_, k) => k !== j));
  };

  // « Livrer à » : confirmation par le client (demande du propriétaire 2026-10-10)
  const saveLivraison = async () => {
    if (!livSame && (liv.address1.trim().length < 3 || liv.city.trim().length < 2 || liv.postal_code.trim().length < 3)) {
      toast.error(t("respond.ship_incomplete"));
      return;
    }
    setLivBusy(true);
    try {
      await api.post(`/quick-quote/${token}/livraison`, livSame ? { same: true } : { same: false, ...liv });
      toast.success(t("respond.ship_saved"));
      setQ((prev) => ({ ...prev, livraison_confirmee: new Date().toISOString(),
                       livraison_meme_adresse: livSame, livraison_a: livSame ? {} : { ...liv } }));
      setLivEdit(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLivBusy(false);
    }
  };
  const setLivField = (k) => (e) => setLiv((f) => ({ ...f, [k]: e.target.value }));
  const livInput = "w-full bg-ink border border-ivory/15 rounded-lg px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300";
  const livLabel = "block text-[10px] uppercase tracking-widest text-ivory/55 mb-1";

  // « Facturé à » : confirmation par le client (demande du propriétaire 2026-10-19)
  const saveFacturation = async () => {
    if (!facSame && (fac.address1.trim().length < 3 || fac.city.trim().length < 2 || fac.postal_code.trim().length < 3)) {
      toast.error(t("respond.bill_incomplete"));
      return;
    }
    setFacBusy(true);
    try {
      await api.post(`/quick-quote/${token}/facturation`, facSame ? { same: true } : { same: false, ...fac });
      toast.success(t("respond.bill_saved"));
      // Recharger : la copie exacte enregistrée (case cochée) s'affiche telle quelle
      const r = await api.get(`/quick-quote/${token}`);
      setQ(r.data);
      setFacEdit(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setFacBusy(false);
    }
  };
  const setFacField = (k) => (e) => setFac((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!sel.length) { toast.error(t("respond.need_one")); return; }
    setBusy(true);
    try {
      await api.post(`/quick-quote/${token}/respond`, {
        selected: sel,
        texts: Object.fromEntries(sel.map((i) => [String(i), texts[i] || ""])),
        methods: Object.fromEntries(sel.map((i) => [String(i), methods[i] || ""])),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Paiement : un COMPTE est requis (demande du propriétaire) — le lien Stripe  // n'est remis qu'au client connecté, et la commande reste dans son dossier.
  const payNow = async () => {
    if (!user) {
      toast.info(t("respond.pay_login_required"));
      navigate("/connexion", { state: { next: `/soumission/${token}` } });
      return;
    }
    setPayBusy(true);
    try {
      // La sélection de modèles part avec la demande : si des modèles sont
      // décochés, le serveur crée une session Stripe au montant des SEULS
      // modèles cochés (demande du propriétaire, 2026-10-28)
      const { data } = await api.post(`/quick-quote/${token}/pay`, { selection: sel });
      window.location.href = data.url;  // redirection sécurisée vers Stripe
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPayBusy(false);
    }
  };

  // « Refuser la soumission » : motif + petite note OBLIGATOIRES → coche « Refusé »
  // dans l'admin et avise commande@trophee.ca par courriel
  const confirmRefuse = async () => {
    if (!refuseMotif) { toast.error(t("respond.refuse_need_motif")); return; }
    if (!refuseNote.trim()) { toast.error(t("respond.refuse_need_note")); return; }
    setRefuseBusy(true);
    try {
      await api.post(`/quick-quote/${token}/refuse`, { motif: refuseMotif, note: refuseNote.trim() });
      setRefuseOpen(false);
      setRefused(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRefuseBusy(false);
    }
  };

  // Retrait de la soumission par le client (demande du propriétaire, 2026-10-06) —
  // raison OBLIGATOIRE ; met fin à TOUTES les relances (soumission, paiement, épreuve).
  // Raison « prix » → CONTRE-OFFRE automatique (rabais proposé avant le retrait) ;
  // « Retirer quand même » renvoie avec force=true.
  const retirer = async (force = false) => {
    if (retireReason.trim().length < 3) { toast.error(t("respond.retire_need_reason")); return; }
    setRetireBusy(true);
    try {
      const { data } = await api.post(`/quick-quote/${token}/retirer`,
        { reason: retireReason.trim(), force });
      if (data.counter_offer) {
        // Contre-offre automatique : le rabais est DÉJÀ appliqué à la soumission —
        // on la propose au client avant de retirer quoi que ce soit
        setRetireOpen(false);
        setCounterOffer(data.pct);
        return;
      }
      setRetireOpen(false);
      setRetired(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRetireBusy(false);
    }
  };

  // Le client accepte la contre-offre : la soumission garde ses NOUVEAUX prix
  const acceptCounterOffer = async () => {
    setCounterOffer(null);
    try {
      // Alerte « Prix chauds » (demande du propriétaire) : l'atelier est avisé
      // IMMÉDIATEMENT par courriel — vente sauvée + badge dans l'admin
      await api.post(`/quick-quote/${token}/accepte-contre-offre`);
    } catch { /* silencieux — l'acceptation restera visible au prochain chargement */ }
    try {
      const r = await api.get(`/quick-quote/${token}`);
      setQ(r.data);
    } catch { /* silencieux — les prix seront à jour au prochain chargement */ }
    toast.success(t("respond.counter_accepted"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (q === false) {
    return (
      <div className="pt-40 pb-28 max-w-xl mx-auto px-6 text-center" data-testid="respond-invalid">
        <p className="text-ivory/70">{t("respond.invalid")}</p>
      </div>
    );
  }
  if (!q) return null;

  const lineTotal = (i) => {
    const it = q.items[i] || {};
    const p = (q.pricing || [])[i] || {};
    const base = (it.qty || 1) * (num(p.product) + num(p.engraving)) + num(p.insert) + num(p.matrix) + num(p.transport);
    if (num(p.rabais_pct)) return Math.max(0, base * (1 - Math.min(num(p.rabais_pct), 100) / 100));
    return Math.max(0, base - num(p.rabais_amt));
  };
  // Rabais de ligne (ex. : « −10 % » ou « −25,00 $ ») — vide si aucun
  const rabaisLabel = (i) => {
    const p = (q.pricing || [])[i] || {};
    if (num(p.rabais_pct)) return `−${p.rabais_pct} %`;
    if (num(p.rabais_amt)) return `−${num(p.rabais_amt).toFixed(2)} $`;
    return "";
  };
  const grandBase = sel.reduce((s, i) => s + lineTotal(i), 0);
  // Prix visibles côté client SEULEMENT une fois remplis par un vendeur/admin
  // (règle propriétaire) : tant que toute la ligne est à 0, on affiche « à venir »
  const ligneRemplie = (i) => {
    const p = (q.pricing || [])[i] || {};
    return num(p.product) > 0 || num(p.engraving) > 0 || num(p.insert) > 0 || num(p.matrix) > 0 || num(p.transport) > 0;
  };
  const prixRemplis = (q.items || []).some((_, i) => ligneRemplie(i));
  // Rabais global (gros volumes) : appliqué après les rabais de ligne, avant taxes
  const grand = (() => {
    if (num(q.global_rabais_pct)) return Math.max(0, grandBase * (1 - Math.min(num(q.global_rabais_pct), 100) / 100));
    return Math.max(0, grandBase - num(q.global_rabais_amt));
  })();
  const globalRabaisLabel = num(q.global_rabais_pct) ? `−${q.global_rabais_pct} %`
    : num(q.global_rabais_amt) ? `−${num(q.global_rabais_amt).toFixed(2)} $` : "";
  // MONTANTS RECALCULÉS sur les SEULS modèles cochés (demande du propriétaire,
  // 2026-10-28 : « recalculer le total affiché au client selon les modèles
  // cochés ») — rabais global en $ au PRORATA (comme au serveur), mêmes taxes ;
  // tout coché (ou sélection vierge) = montants du lien Stripe habituels
  const nbItems = (q.items || []).length;
  const selPartielle = prixRemplis && sel.length > 0 && sel.length < nbItems;
  const subtotalTout = (q.items || []).reduce((s, _, i) => s + lineTotal(i), 0);
  const partSel = subtotalTout > 0 ? Math.min(1, grandBase / subtotalTout) : 1;
  const netSel = num(q.global_rabais_pct)
    ? grand * 1  // % déjà proportionnel (grand = grandBase après %)
    : Math.max(0, grandBase - num(q.global_rabais_amt) * partSel);
  const tpsSel = q.tax_exempt ? 0 : Math.round(netSel * 0.05 * 100) / 100;
  const tvqSel = q.tax_exempt ? 0 : Math.round(netSel * 0.09975 * 100) / 100;
  const affSub = selPartielle ? Math.round(netSel * 100) / 100 : Number(q.payment?.subtotal || 0);
  const affTps = selPartielle ? tpsSel : Number(q.payment?.tps || 0);
  const affTvq = selPartielle ? tvqSel : Number(q.payment?.tvq || 0);
  const affTotal = selPartielle ? Math.round((netSel + tpsSel + tvqSel) * 100) / 100 : Number(q.payment?.amount || 0);

  if (done) {
    return (
      <div className="pt-40 pb-28 max-w-xl mx-auto px-6 text-center" data-testid="respond-sent">
        <SEO title={t("respond.seo_title")} description={t("respond.seo_desc")} path="/soumission" noindex />
        <Zap className="w-10 h-10 text-gold mx-auto mb-5" />
        <h1 className="font-serif text-3xl sm:text-4xl mb-4">{t("respond.sent_title")}</h1>
        <p className="text-ivory/65 text-sm leading-relaxed mb-8">{t("respond.sent_text")}</p>
        <button onClick={() => setDone(false)} data-testid="respond-edit"
          className="text-xs text-gold underline underline-offset-4 hover:text-champagne transition-colors">
          {t("respond.resubmit")}
        </button>
      </div>
    );
  }

  if (refused) {
    return (
      <div className="pt-40 pb-28 max-w-xl mx-auto px-6 text-center" data-testid="respond-refused-sent">
        <SEO title={t("respond.seo_title")} description={t("respond.seo_desc")} path="/soumission" noindex />
        <X className="w-10 h-10 text-red-400 mx-auto mb-5" />
        <h1 className="font-serif text-3xl sm:text-4xl mb-4">{t("respond.refuse_sent_title")}</h1>
        <p className="text-ivory/65 text-sm leading-relaxed">{t("respond.refuse_sent_text")}</p>
      </div>
    );
  }

  // Soumission RETIRÉE par le client (demande du propriétaire, 2026-10-06) :
  // écran de fin dédié — aucune relance ne partira plus pour ce dossier
  if (retired || q.retire) {
    return (
      <div className="pt-40 pb-28 max-w-xl mx-auto px-6 text-center" data-testid="respond-retired">
        <SEO title={t("respond.seo_title")} description={t("respond.seo_desc")} path="/soumission" noindex />
        <Check className="w-10 h-10 text-ivory/60 mx-auto mb-5" />
        <h1 className="font-serif text-3xl sm:text-4xl mb-4">{t("respond.retire_sent_title")}</h1>
        <p className="text-ivory/65 text-sm leading-relaxed">{t("respond.retire_sent_text")}</p>
      </div>
    );
  }

  return (
    <div data-testid="respond-page" className="pt-32 pb-28">
      <SEO title={t("respond.seo_title")} description={t("respond.seo_desc")} path="/soumission" noindex />
      <div className="max-w-3xl mx-auto px-6">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Mr Trophée</p>
          <h1 className="font-serif text-4xl sm:text-5xl mb-3">
            {t("respond.title_1")} <span className="text-gold italic">{t("respond.title_2")}</span>
          </h1>
          <p className="text-ivory/70 text-base mb-1">{t("respond.hello").replace("{name}", q.name)}</p>
          <p className="text-ivory/55 text-sm mb-2">{t("respond.intro")}</p>
          {q.needed_by && (
            <p className="text-amber-300 text-sm mb-2">{t("respond.needed_by")} <strong>{q.needed_by}</strong></p>
          )}
          <p className="text-ivory/45 text-xs mb-8">{t("respond.choose_hint")}</p>
          {acceptedBanner && (
            <p className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm px-5 py-3.5 mb-6"
              data-testid="respond-accepted-notice">{t("respond.accept_thanks")}</p>
          )}
          {/* Confirmation HUMAINE d'acceptation (demande du propriétaire,
              2026-10-22) : le lien du courriel ouvre ce panneau — « Accepté » ne
              se coche que par CE bouton (POST), jamais au simple suivi du lien */}
          {accepterInvite && !q.accepte && (
            <div className="rounded-xl border-2 border-gold bg-gold/10 p-5 sm:p-6 mb-6"
              data-testid="respond-accept-confirm">
              <p className="text-gold font-semibold text-base mb-1">{t("respond.accept_confirm_title")}</p>
              <p className="text-ivory/60 text-xs mb-4">{t("respond.accept_confirm_hint")}</p>
              <button type="button" disabled={acceptBusy} data-testid="respond-accept-btn"
                onClick={async () => {
                  setAcceptBusy(true);
                  try {
                    await api.post(`/quick-quote/${token}/accept`);
                    const { data } = await api.get(`/quick-quote/${token}`);
                    setQ(data);
                    setAccepterInvite(false);
                    setAcceptedBanner(true);
                    toast.success(t("respond.accept_thanks"));
                  } catch { toast.error(t("respond.error")); }
                  finally { setAcceptBusy(false); }
                }}
                className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors disabled:opacity-60">
                {acceptBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {t("respond.accept_confirm_btn")}
              </button>
            </div>
          )}
          {q.discount_offered && (
            <p className="rounded-xl border border-gold/40 bg-gold/10 text-gold text-sm px-5 py-3.5 mb-6"
              data-testid="respond-discount-notice">{t("respond.discount_notice", { pct: q.discount_pct || 5 })}</p>
          )}
          {!q.discount_offered && q.refused && (
            <p className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-5 py-3.5 mb-6"
              data-testid="respond-refused-notice">{t("respond.refused_notice")}</p>
          )}
        </FadeUp>

        {/* Adresses (Facturé à + Livrer à) : après acceptation, ou dès que
            l'atelier les demande via « SVP confirmer votre adresse » (2026-10-19) */}
        {(q.accepte || q.addr_request_last) && (
          <FadeUp delay={0.04}>
            <div className="rounded-xl border border-gold/25 bg-surface p-5 sm:p-6 mb-8" data-testid="respond-shipping">
              <p className="text-xs uppercase tracking-widest text-gold mb-1 flex items-center gap-2">
                <Truck className="w-4 h-4" /> {t("respond.ship_title")}
              </p>
              <p className="text-[11px] text-ivory/50 mb-5">{t("respond.ship_note")}</p>
              <div className="grid sm:grid-cols-2 gap-5">
                {/* Facturé à — confirmé par le client (demande du propriétaire
                    2026-10-19) : case « copier l'adresse de ma demande » (défaut)
                    OU nouvelle adresse complète */}
                <div data-testid="respond-billing">
                  <p className={livLabel}>{t("respond.ship_billing")}</p>
                  {q.facture_confirmee && !facEdit ? (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm" data-testid="respond-bill-confirmed">
                      <div className="text-ivory/85 leading-relaxed">
                        {(() => {
                          const f = (q.facture_a && q.facture_a.address1) ? q.facture_a : (q.client_address || {});
                          return (
                            <>
                              <p className="font-semibold text-ivory">{f.name || q.name}</p>
                              {f.address1 && <p>{f.address1}{f.address2 ? `, ${f.address2}` : ""}</p>}
                              {(f.city || f.postal_code) && (
                                <p>{[f.city, f.province].filter(Boolean).join(", ")}{f.postal_code ? `  ${f.postal_code}` : ""}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <p className="text-emerald-300 flex items-center gap-2 mt-1.5">
                        <Check className="w-4 h-4" />
                        {q.facture_meme_adresse !== false ? t("respond.bill_confirmed_same") : t("respond.bill_confirmed_new")}
                      </p>
                      <button type="button" onClick={() => setFacEdit(true)} data-testid="respond-bill-edit"
                        className="mt-2 text-xs text-gold underline underline-offset-4 hover:text-champagne transition-colors">
                        {t("respond.ship_edit")}
                      </button>
                    </div>
                  ) : (
                    <div data-testid="respond-bill-form">
                      {/* Adresse en dossier — celle qui sera COPIÉE si la case reste cochée */}
                      <div className="rounded-lg border border-ivory/10 bg-ink px-4 py-3 text-sm text-ivory/80 leading-relaxed mb-3">
                        {(() => {
                          const f = (q.facture_a && q.facture_a.address1) ? q.facture_a : (q.client_address || {});
                          return (
                            <>
                              <p className="font-semibold text-ivory">{f.name || q.name}</p>
                              {f.address1 && <p>{f.address1}{f.address2 ? `, ${f.address2}` : ""}</p>}
                              {(f.city || f.postal_code) && (
                                <p>{[f.city, f.province].filter(Boolean).join(", ")}{f.postal_code ? `  ${f.postal_code}` : ""}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <label className="flex items-center gap-2.5 cursor-pointer select-none mb-3" data-testid="respond-bill-same-label">
                        <input type="checkbox" checked={facSame} onChange={(e) => setFacSame(e.target.checked)}
                          data-testid="respond-bill-same" className="w-4 h-4 accent-[#d4af37]" />
                        <span className="text-sm text-ivory/85">{t("respond.bill_same")}</span>
                      </label>
                      {!facSame && (
                        <div className="space-y-2.5 mb-3">
                          <input value={fac.name} onChange={setFacField("name")} maxLength={120}
                            placeholder={t("respond.bill_name")} data-testid="respond-bill-name" className={livInput} />
                          <input value={fac.address1} onChange={setFacField("address1")} maxLength={200} required
                            placeholder={`${t("respond.ship_address")} *`} data-testid="respond-bill-address" className={livInput} />
                          <input value={fac.address2} onChange={setFacField("address2")} maxLength={200}
                            placeholder={t("respond.ship_address2")} data-testid="respond-bill-address2" className={livInput} />
                          <div className="grid grid-cols-2 gap-2.5">
                            <input value={fac.city} onChange={setFacField("city")} maxLength={100} required
                              placeholder={`${t("respond.ship_city")} *`} data-testid="respond-bill-city" className={livInput} />
                            <input value={fac.province} onChange={setFacField("province")} maxLength={2}
                              placeholder={t("respond.ship_province")} data-testid="respond-bill-province" className={livInput} />
                          </div>
                          <input value={fac.postal_code} onChange={setFacField("postal_code")} maxLength={12} required
                            placeholder={`${t("respond.ship_postal")} *`} data-testid="respond-bill-postal" className={livInput} />
                        </div>
                      )}
                      <button type="button" onClick={saveFacturation} disabled={facBusy} data-testid="respond-bill-save"
                        className="inline-flex items-center gap-2 bg-gold text-ink text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                        {facBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {facBusy ? t("respond.ship_saving") : t("respond.bill_save")}
                      </button>
                    </div>
                  )}
                </div>
                {/* Livrer à — confirmé par le client */}
                <div data-testid="respond-deliver">
                  <p className={livLabel}>{t("respond.ship_to")}</p>
                  {q.livraison_confirmee && !livEdit ? (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm" data-testid="respond-ship-confirmed">
                      {q.livraison_meme_adresse !== false ? (
                        <p className="text-emerald-300 flex items-center gap-2"><Check className="w-4 h-4" /> {t("respond.ship_confirmed_same")}</p>
                      ) : (
                        <div className="text-ivory/85 leading-relaxed">
                          <p className="font-semibold">{(q.livraison_a || {}).name}</p>
                          <p>{(q.livraison_a || {}).address1}{(q.livraison_a || {}).address2 ? `, ${q.livraison_a.address2}` : ""}</p>
                          <p>{[(q.livraison_a || {}).city, (q.livraison_a || {}).province].filter(Boolean).join(", ")}  {(q.livraison_a || {}).postal_code}</p>
                        </div>
                      )}
                      <button type="button" onClick={() => setLivEdit(true)} data-testid="respond-ship-edit"
                        className="mt-2 text-xs text-gold underline underline-offset-4 hover:text-champagne transition-colors">
                        {t("respond.ship_edit")}
                      </button>
                    </div>
                  ) : (
                    <div data-testid="respond-ship-form">
                      <label className="flex items-center gap-2.5 cursor-pointer select-none mb-3" data-testid="respond-ship-same-label">
                        <input type="checkbox" checked={livSame} onChange={(e) => setLivSame(e.target.checked)}
                          data-testid="respond-ship-same" className="w-4 h-4 accent-[#d4af37]" />
                        <span className="text-sm text-ivory/85">{t("respond.ship_same")}</span>
                      </label>
                      {!livSame && (
                        <div className="space-y-2.5 mb-3">
                          <input value={liv.name} onChange={setLivField("name")} maxLength={120}
                            placeholder={t("respond.ship_name")} data-testid="respond-ship-name" className={livInput} />
                          <input value={liv.address1} onChange={setLivField("address1")} maxLength={200} required
                            placeholder={`${t("respond.ship_address")} *`} data-testid="respond-ship-address" className={livInput} />
                          <input value={liv.address2} onChange={setLivField("address2")} maxLength={200}
                            placeholder={t("respond.ship_address2")} data-testid="respond-ship-address2" className={livInput} />
                          <div className="grid grid-cols-2 gap-2.5">
                            <input value={liv.city} onChange={setLivField("city")} maxLength={100} required
                              placeholder={`${t("respond.ship_city")} *`} data-testid="respond-ship-city" className={livInput} />
                            <input value={liv.province} onChange={setLivField("province")} maxLength={2}
                              placeholder={t("respond.ship_province")} data-testid="respond-ship-province" className={livInput} />
                          </div>
                          <input value={liv.postal_code} onChange={setLivField("postal_code")} maxLength={12} required
                            placeholder={`${t("respond.ship_postal")} *`} data-testid="respond-ship-postal" className={livInput} />
                        </div>
                      )}
                      <button type="button" onClick={saveLivraison} disabled={livBusy} data-testid="respond-ship-save"
                        className="inline-flex items-center gap-2 bg-gold text-ink text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                        {livBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                        {livBusy ? t("respond.ship_saving") : t("respond.ship_save")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </FadeUp>
        )}

        <FadeUp delay={0.08}>
          {/* Consigne de sélection (demande du propriétaire, 2026-10-27 :
              « si c'est le cas, cocher la ou les cases de modèle que vous
              désirez ») — affichée dès qu'il y a plus d'un modèle */}
          {(q.items || []).length > 1 && (
            <p className="text-sm text-gold font-semibold mb-3" data-testid="respond-choice-hint">
              {t("respond.choice_hint")}
            </p>
          )}
          <div className="space-y-4 mb-6">
            {(q.items || []).map((it, i) => {
              const p = (q.pricing || [])[i] || {};
              const on = sel.includes(i);
              return (
                <div key={i} data-testid={`respond-item-${i}`}
                  className={`rounded-xl border p-5 cursor-pointer transition-all duration-300 ${
                    on ? "border-gold bg-gold/10 shadow-lg shadow-gold/10" : "border-ivory/15 bg-surface hover:border-gold/40"}`}
                  onClick={() => toggle(i)}>
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                      on ? "bg-gold border-gold text-ink" : "border-ivory/40 text-transparent"}`}
                      data-testid={`respond-item-case-${i}`}>
                      <Check className="w-4 h-4" />
                    </span>
                    {/* Vignette du produit (demande du propriétaire, 2026-10-30) :
                        à GAUCHE de la ligne d'info — 1re image jointe par l'admin */}
                    {(() => {
                      const img = [it.logo_path, ...(it.attachments || [])].find((p) =>
                        p && /\.(png|jpe?g|webp|gif|svg)$/i.test(String(p)));
                      return img ? (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`} target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`respond-item-thumb-${i}`}
                          className="shrink-0 w-14 h-14 rounded-lg border border-gold/40 overflow-hidden bg-white hover:border-gold transition-colors duration-300">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${img}`} alt={it.product_name || "Produit"}
                            className="w-full h-full object-contain" />
                        </a>
                      ) : null;
                    })()}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ivory font-semibold">
                        {t("quote.choice").replace("{n}", i + 1)} — {it.catalogue}
                        {it.product_name ? ` · ${it.product_name}` : it.page ? ` · p. ${it.page}` : ""}{it.product_no ? ` · nº ${it.product_no}` : ""}
                        {" · "}<strong>×{it.qty}</strong>
                      </p>
                      {ligneRemplie(i) ? (
                        <>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-ivory/55">
                            <span>{t("respond.col_product")} : {money(num(p.product))}{t("respond.per_unit")}</span>
                            <span>{t("respond.col_engraving")} : {money(num(p.engraving))}{t("respond.per_unit")}</span>
                            <span>{t("respond.col_insert")} : {money(num(p.insert))}</span>
                            <span>{t("respond.col_matrix")} : {money(num(p.matrix))}</span>
                            <span>{t("respond.col_transport")} : {money(num(p.transport))}</span>
                          </div>
                          <p className="text-sm text-champagne mt-1" data-testid={`respond-line-total-${i}`}>
                            {t("respond.total_line")} : <strong>{money(lineTotal(i))}</strong>
                            {rabaisLabel(i) && (
                              <span className="ml-2 text-xs text-emerald-400" data-testid={`respond-rabais-${i}`}>
                                rabais {rabaisLabel(i)}
                              </span>
                            )}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-ivory/50 italic mt-1.5" data-testid={`respond-price-pending-${i}`}>
                          {t("respond.price_pending_line")}
                        </p>
                      )}
                      {on && (
                        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                          {/* Type d'inscription (demande du propriétaire) : gravure au
                              laser OU impression sérigraphie — choisi par le client */}
                          <p className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">
                            {t("respond.method_label")}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-3" data-testid={`respond-method-${i}`}>
                            {[["laser", t("respond.method_laser")], ["serigraphie", t("respond.method_serigraphie")]].map(([k, lab]) => (
                              <button key={k} type="button"
                                onClick={() => setMethods((x) => ({ ...x, [i]: x[i] === k ? "" : k }))}
                                data-testid={`respond-method-${k}-${i}`}
                                aria-pressed={methods[i] === k}
                                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors duration-300 ${
                                  methods[i] === k
                                    ? "bg-gold text-ink border-gold"
                                    : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                                {lab}
                              </button>
                            ))}
                          </div>
                          <label htmlFor={`engraving-${i}`} className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1">
                            {t("respond.engraving_label")}
                          </label>
                          <textarea id={`engraving-${i}`} rows={2} maxLength={2000}
                            data-testid={`respond-text-${i}`}
                            value={texts[i] || ""}
                            onChange={(e) => setTexts((x) => ({ ...x, [i]: e.target.value }))}
                            placeholder={t("respond.engraving_ph")}
                            className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Case « recevoir les étapes par texto » + code à 6 chiffres (demande
              du propriétaire, 2026-10-30) — numéro VÉRIFIÉ avant tout envoi auto */}
          <div className="mb-6">
            <SmsEtapesBox phone={q.phone || (q.client_address || {}).phone || ""}
              quoteToken={token}
              verified={(q.sms_etapes || {}).verified} />
          </div>

          <div className="rounded-xl border border-ivory/15 bg-surface p-5 mb-6" data-testid="respond-logo-block">
            <label htmlFor="respond-logo" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
              {t("respond.logo_label")}
            </label>
            <p className="text-[11px] text-ivory/45 mb-3">{t("respond.logo_hint")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-full text-xs cursor-pointer hover:bg-gold hover:text-ink transition-colors duration-300">
                {logoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                {logoBusy ? t("respond.logo_uploading") : t("respond.logo_label")}
                <input id="respond-logo" type="file" accept="image/*,.pdf" multiple className="hidden"
                  data-testid="respond-logo-input" onChange={uploadLogo} disabled={logoBusy} />
              </label>
              {logos.map((lg, j) => (
                <span key={j} data-testid={`respond-logo-name-${j}`}
                  className="inline-flex items-center gap-1.5 text-xs text-emerald-300 border border-emerald-400/30 rounded-full px-3 py-1">
                  {lg.name || lg.url.split("/").pop()}
                  <button type="button" onClick={() => removeLogo(j)}
                    data-testid={`respond-logo-remove-${j}`} title={t("respond.logo_remove")}
                    className="text-ivory/50 hover:text-red-400 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-6">
            <p className="text-sm text-ivory/70" data-testid="respond-selected-count">
              {lang === "en"
                ? `${sel.length} ${sel.length === 1 ? "selected product" : "selected products"}`
                : `${plur(sel.length, "produit")} ${sel.length > 1 ? "sélectionnés" : "sélectionné"}`}
            </p>
            {prixRemplis ? (
              <p className="text-lg text-champagne" data-testid="respond-grand-total">
                {t("respond.grand_total")} : <strong>{money(grand)}</strong>
                {globalRabaisLabel && (
                  <span className="ml-2 text-sm text-emerald-400" data-testid="respond-global-rabais">
                    {t("respond.global_rabais")} {globalRabaisLabel}
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm text-ivory/50 italic" data-testid="respond-price-pending-total">
                {t("respond.price_pending_total")}
              </p>
            )}
          </div>

          {/* Paiement Stripe : COMPTE REQUIS (demande du propriétaire) — le bouton
              demande connexion/inscription, puis le serveur remet le lien Stripe au
              client connecté ; « Payé » se coche tout seul côté
              admin dès réception (webhook) */}
          {q.payment?.amount > 0 && !q.paid && (
            <div className="mb-6">
              {/* Détail des taxes AVANT le clic « Payer » (demande du propriétaire) :
                  sous-total après rabais + TPS 5 % + TVQ 9,975 % = total Stripe.
                  Modèles décochés (2026-10-28) : montants RECALCULÉS sur la
                  sélection — le serveur facture exactement ces montants */}
              <div className="bg-ink border border-ivory/10 rounded-xl px-5 py-4 mb-4 text-sm space-y-1.5"
                data-testid="respond-taxes">
                <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-2">
                  {t("respond.taxes_title")}
                  {selPartielle && (
                    <span className="ml-2 text-gold normal-case tracking-normal" data-testid="respond-taxes-selection-note">
                      — recalculé sur les modèles cochés
                    </span>
                  )}
                </p>
                <p className="flex justify-between text-ivory/70" data-testid="respond-taxes-subtotal">
                  <span>{t("respond.taxes_subtotal")}</span>
                  <span>{money(affSub)}</span>
                </p>
                {!selPartielle && Number(q.payment.welcome_rabais?.amount || 0) > 0 && (
                  <p className="flex justify-between text-emerald-300" data-testid="respond-taxes-welcome">
                    <span>{t("respond.taxes_welcome")}</span>
                    <span>−{money(q.payment.welcome_rabais.amount)}</span>
                  </p>
                )}
                <p className="flex justify-between text-ivory/70" data-testid="respond-taxes-tps">
                  <span>{t("respond.taxes_tps")}</span>
                  <span>{money(affTps)}</span>
                </p>
                <p className="flex justify-between text-ivory/70" data-testid="respond-taxes-tvq">
                  <span>{t("respond.taxes_tvq")}</span>
                  <span>{money(affTvq)}</span>
                </p>
                <p className="flex justify-between text-champagne font-semibold border-t border-ivory/10 pt-1.5"
                  data-testid="respond-taxes-total">
                  <span>{t("respond.taxes_total")}</span>
                  <span>{money(affTotal)}</span>
                </p>
              </div>
              <button onClick={payNow} disabled={payBusy} data-testid="respond-pay"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 text-ink font-bold px-8 py-4 rounded-full hover:bg-emerald-400 transition-colors duration-300 disabled:opacity-60">
                {payBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                {t("respond.pay")} — {money(affTotal)}
              </button>
              <p className="text-[11px] text-ivory/50 text-center mt-2" data-testid="respond-pay-note">
                {t("respond.pay_account_note")}
              </p>
            </div>
          )}
          {q.paid && (
            <p className="mb-6 text-center text-sm text-emerald-300 border border-emerald-500/40 rounded-full px-6 py-3"
              data-testid="respond-paid">{t("respond.paid")}</p>
          )}

          <button onClick={submit} disabled={busy || logoBusy} data-testid="respond-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? t("respond.sending") : t("respond.submit")}
          </button>

          {/* Refus côté client : motif + petite note OBLIGATOIRES (popup) — caché
              quand la soumission est déjà refusée sans contre-offre en cours */}
          {(!q.refused || q.discount_offered) && (
            <button type="button"
              onClick={() => { setRefuseMotif(""); setRefuseNote(""); setRefuseOpen(true); }}
              data-testid="respond-refuse-open"
              className="w-full mt-4 text-center text-xs text-ivory/45 hover:text-red-300 underline underline-offset-4 transition-colors">
              {t("respond.refuse_btn")}
            </button>
          )}
          {/* Retirer sa soumission (demande du propriétaire, 2026-10-06) : raison
              OBLIGATOIRE — met fin à TOUTES les relances. Masqué si déjà payé :
              une vente payée s'annule par remboursement, en nous contactant */}
          {!q.paid && (
            <button type="button"
              onClick={() => { setRetireReason(""); setRetireOpen(true); }}
              data-testid="respond-retire-open"
              className="w-full mt-2 text-center text-xs text-ivory/35 hover:text-amber-300 underline underline-offset-4 transition-colors">
              {t("respond.retire_btn")}
            </button>
          )}
        </FadeUp>
      </div>

      {/* Popup « Retirer ma soumission » (demande du propriétaire, 2026-10-06) :
          raison OBLIGATOIRE → « Retirée par le client » coché dans l'admin,
          courriel à l'atelier avec la raison, FIN de toutes les relances */}
      {retireOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="respond-retire-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setRetireOpen(false)} aria-label="Fermer" />
          <div data-lenis-prevent className="relative bg-surface border border-amber-500/30 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setRetireOpen(false)} data-testid="respond-retire-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-2">{t("respond.retire_title")}</h3>
            <p className="text-xs text-ivory/55 mb-5 leading-relaxed">{t("respond.retire_intro")}</p>
            <label htmlFor="retire-reason" className="block text-xs uppercase tracking-widest text-ivory/60 mb-2">
              {t("respond.retire_reason_label")}
            </label>
            <textarea id="retire-reason" rows={4} maxLength={1000} value={retireReason}
              onChange={(e) => setRetireReason(e.target.value)} data-testid="respond-retire-reason"
              placeholder={t("respond.retire_reason_ph")}
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-amber-400 focus:outline-none transition-colors duration-300 mb-5" />
            <button onClick={() => retirer()} disabled={retireBusy || retireReason.trim().length < 3}
              data-testid="respond-retire-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-500/90 text-ink font-bold px-6 py-3.5 rounded-full hover:bg-amber-400 transition-colors duration-300 disabled:opacity-60">
              {retireBusy && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("respond.retire_confirm")}
            </button>
            <button onClick={() => setRetireOpen(false)} disabled={retireBusy} data-testid="respond-retire-cancel"
              className="w-full mt-3 text-center text-xs text-ivory/50 hover:text-gold transition-colors duration-300">
              {t("respond.retire_cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Popup CONTRE-OFFRE automatique (demande du propriétaire, 2026-10-06) :
          un retrait pour raison de prix propose d'abord le rabais — le client
          accepte (prix déjà révisés) ou retire quand même (force) */}
      {counterOffer !== null && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="respond-counter-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setCounterOffer(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/40 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 text-center">
            <p className="text-gold tracking-[0.3em] uppercase text-xs mb-3">{t("respond.counter_title")}</p>
            <p className="font-serif text-5xl text-gold mb-3" data-testid="respond-counter-pct">−{counterOffer} %</p>
            <p className="text-sm text-ivory/70 leading-relaxed mb-6">
              {t("respond.counter_text", { pct: counterOffer })}
            </p>
            <button onClick={acceptCounterOffer} data-testid="respond-counter-accept"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300">
              <Check className="w-4 h-4" />
              {t("respond.counter_accept", { pct: counterOffer })}
            </button>
            <button onClick={() => { setCounterOffer(null); retirer(true); }} disabled={retireBusy}
              data-testid="respond-counter-decline"
              className="w-full mt-3 text-center text-xs text-ivory/50 hover:text-red-300 underline underline-offset-4 transition-colors duration-300 disabled:opacity-60">
              {retireBusy ? "…" : t("respond.counter_decline")}
            </button>
          </div>
        </div>
      )}

      {/* Popup « Refuser la soumission » : motif + note obligatoires → courriel à
          commande@trophee.ca + « Refusé » coché dans l'admin */}
      {refuseOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="respond-refuse-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setRefuseOpen(false)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60">
            <button onClick={() => setRefuseOpen(false)} data-testid="respond-refuse-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">{t("respond.refuse_title")}</h3>
            <p className="text-xs text-ivory/55 mb-5">{t("respond.refuse_intro")}</p>
            <div className="space-y-2 mb-4">
              {["prix", "delai", "annule", "autre"].map((k) => (
                <label key={k} data-testid={`respond-refuse-motif-${k}`}
                  className={`flex items-center gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors duration-300 ${
                    refuseMotif === k ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}>
                  <input type="radio" name="respond-refuse-motif" className="accent-[#d4af37]"
                    checked={refuseMotif === k} onChange={() => setRefuseMotif(k)} />
                  <span className="text-sm">{t(`respond.refuse_motif_${k}`)}</span>
                </label>
              ))}
            </div>
            <label htmlFor="respond-refuse-note" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
              {t("respond.refuse_note_label")} *
            </label>
            <textarea id="respond-refuse-note" rows={3} maxLength={1000}
              data-testid="respond-refuse-note"
              value={refuseNote} onChange={(e) => setRefuseNote(e.target.value)}
              placeholder={t("respond.refuse_note_ph")}
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300 mb-5" />
            <button onClick={confirmRefuse} disabled={refuseBusy} data-testid="respond-refuse-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500/90 text-ivory font-bold px-6 py-3.5 rounded-full hover:bg-red-500 transition-colors duration-300 disabled:opacity-60">
              {refuseBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              {refuseBusy ? t("respond.sending") : t("respond.refuse_confirm")}
            </button>
            <button onClick={() => setRefuseOpen(false)} data-testid="respond-refuse-cancel"
              className="w-full mt-2.5 text-center text-xs text-ivory/55 hover:text-gold underline underline-offset-2 transition-colors">
              {t("respond.refuse_cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
